import React, { useId } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { AvatarByName } from './avatar';
import { BoardCards, HoleCards } from './Cards';
import { formatClockTime, formatMetadata, formatPot } from './format';
import { HAND_CARD_TOGGLE_ATTR, HandTitle, RunRows, WinSummaryText } from './HandCard';
import { HandCardBody, useHasMounted } from './HandCardBody';
import { PlayByPlayList } from './PlayByPlayList';
import { ActiveHand, CardRef } from './types';
import { RunResult } from './winSummary';

import styles from './HandHistory.module.scss';

const byOrder = (events: ActiveHand['playByPlay']) => [...events].sort((a, b) => a.order - b.order);

/** A run's payout row carries what that run paid, not the hand's pot. */
const PAYOUT_ROW = /^\s*Payout\s+\d+\s+[—–-]\s/i;

const runOutLabel = (runs: number) => (runs === 2 ? 'Run it twice' : `Run it ${runs} times`);

/**
 * The hand in progress: first in the list, never pinned. Its play-by-play is
 * the one thing in the panel that moves while the player watches, so it opens
 * expanded — and, since the 24 Sep demo, it collapses like the completed cards
 * do: on a phone a player reading back through the last hand does not want the
 * live rows pushing it around (Cord, Justin, Victor).
 */
export const ActiveHandCard = ({
  hand,
  avatarByName,
  holeCards,
  expanded = true,
  onToggle,
  winSummary,
  runResults
}: {
  hand: ActiveHand;
  avatarByName?: AvatarByName;
  /**
   * The viewer's own hole cards from the table, so the live card's header reads
   * like a completed card's — your cards, then the board — and the pre-flop
   * round shows them inline (Jason, 28 Sep).
   */
  holeCards?: CardRef[];
  expanded?: boolean;
  onToggle?: (event?: React.SyntheticEvent<HTMLElement>) => void;
  /** What the viewer has won so far ("You won: Main (Run 1)"), shown in the pot's place. */
  winSummary?: string;
  /** Each paid run's result line for the play-by-play's run headers (see PlayByPlayList). */
  runResults?: Record<number, RunResult>;
}) => {
  // Per instance, not a constant: grid view mounts a panel per table and a
  // closed panel stays mounted, so several live cards can be in the page at
  // once and each toggle must point at its own body.
  const bodyId = `hand-card-live-${useId()}`;
  const onScreen = useHasMounted();
  const events = byOrder(hand.playByPlay);
  const latestPot = [...events]
    .reverse()
    .find((event) => typeof event.totalPot === 'number' && !PAYOUT_ROW.test(event.text));
  const startedAt = events.find((event) => event.ts)?.ts;
  // Announce only the newest action, not the whole list, as the hand plays.
  const latestEvent = events[events.length - 1];
  // A run-out shows a row per board, like a completed multi-run hand.
  const boards = hand.boards && hand.boards.length > 1 ? hand.boards : [hand.board];
  const isRunOut = boards.length > 1;

  return (
    <article
      className={`${styles.handCard} ${styles.handCardLive} ${
        expanded ? styles.handCardExpanded : ''
      }`}
      aria-label='Current hand'>
      <button
        type='button'
        className={styles.handCardHeader}
        aria-expanded={expanded}
        aria-controls={bodyId}
        {...{ [HAND_CARD_TOGGLE_ATTR]: '' }}
        onClick={onToggle}>
        <span className={styles.handCardTitle}>
          <h2>{hand.gameNo ? <HandTitle gameNo={hand.gameNo} /> : 'Next hand'}</h2>
          <span className={styles.livePill}>Live</span>
          {isRunOut && <span className={styles.runPill}>{runOutLabel(boards.length)}</span>}
          {startedAt && <span className={styles.handCardTime}>{formatClockTime(startedAt)}</span>}
        </span>
        <span className={styles.handCardCards}>
          {isRunOut ? (
            // As the completed card: the hole cards once, then a row per run
            // (EN-11110 A1/A2).
            <>
              {holeCards && holeCards.length > 0 && (
                <HoleCards cards={holeCards} revealed size='base' />
              )}
              <RunRows
                boards={boards}
                renderBoard={(board) => (
                  <BoardCards cards={board} emptyText='Dealing' size='base' />
                )}
              />
            </>
          ) : (
            <span className={styles.handCardRun}>
              {holeCards && holeCards.length > 0 && (
                <HoleCards cards={holeCards} revealed size='base' />
              )}
              <span className={styles.handCardBoard}>
                <BoardCards cards={hand.board} emptyText='Pre-flop' size='base' />
              </span>
            </span>
          )}
        </span>
        <span className={styles.handCardMoney}>
          <span className={styles.handCardMeta}>
            {formatMetadata(hand.metadata, hand.gameType)}
          </span>
          {/* No result figure while the hand plays: the one result is the hand
              net (EN-11110 A3), which is not known until it ends. What has
              been paid so far is named below ("You won: Main (Run 1)"). */}
          {winSummary ? (
            <span className={`${styles.handCardPot} ${styles.handCardWinSummary}`}>
              <WinSummaryText text={winSummary} />
            </span>
          ) : (
            <span className={styles.handCardPot}>
              {latestPot ? `Pot ${formatPot(latestPot.totalPot as number, hand.gameType)}` : ''}
            </span>
          )}
        </span>
        <span
          className={`${styles.rowChevron} ${expanded ? styles.rowChevronOpen : ''}`}
          aria-hidden='true'>
          <KeyboardArrowDownIcon />
        </span>
      </button>
      <span className={styles.srOnly} aria-live='polite' aria-atomic='true'>
        {/* Keyed by event identity: a new event with repeated wording (another
            "checks") still replaces the node, so it is announced again. */}
        {expanded && latestEvent && (
          <span key={`${hand.gameNo}-${latestEvent.id}`}>Latest: {latestEvent.text}</span>
        )}
      </span>
      {expanded && (
        <HandCardBody id={bodyId} unfold={onScreen}>
          <div className={styles.playByPlayToggleStatic}>
            <span>Play-by-play</span>
            <strong>{events.length} events</strong>
          </div>
          <PlayByPlayList
            events={events}
            gameType={hand.gameType}
            avatarByName={avatarByName}
            holeCards={holeCards}
            runResults={runResults}
            runBoards={isRunOut ? boards : undefined}
          />
        </HandCardBody>
      )}
    </article>
  );
};
